import { useState, useEffect } from 'react'
import { ChartBarIcon, ClockIcon, BoltIcon, XCircleIcon, SparklesIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { API_BASE_URL } from '../../config/api'
import { Card, CardHeader, EmptyState, Progress, Skeleton, PillButton } from '../dashboard/primitives'
import { FolderStatCard } from '../dashboard/FolderStatCard'
import { LineChart, StackedBarChart, ProgressRing, Legend } from '../dashboard/charts'
import { STEP_META, shortDay } from './DeveloperOverview'

// ─── Types ──────────────────────────────────────────────────

interface DailyVolume { date: string; total: number; verified: number; failed: number; success_rate: number }
interface RejectionBreakdown { reason: string; count: number; percentage: number }
interface DailyLatency { date: string; p50: number; p95: number }
interface FunnelStep { step: string; count: number; percentage: number }
interface DailyWebhooks { date: string; delivered: number; failed: number }

interface AnalyticsData {
  daily_volume: DailyVolume[]
  rejection_breakdown: RejectionBreakdown[]
  daily_latency: DailyLatency[]
  quota: { used: number; limit: number | null }
  funnel: FunnelStep[]
  daily_webhooks: DailyWebhooks[]
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0)
const humanReason = (r: string) => {
  const s = r.replace(/_/g, ' ').toLowerCase()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ─── Main Component ─────────────────────────────────────────

export function AnalyticsCharts({ token }: { token: string }) {
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function fetchAnalytics() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/developer/analytics`, {
          headers: token === 'session' ? {} : { Authorization: `Bearer ${token}` },
          credentials: 'include' as RequestCredentials,
        })
        if (!res.ok) throw new Error('Failed to fetch analytics')
        const json = await res.json()
        if (!cancelled) setData(json)
      } catch (err) {
        console.error('Analytics fetch failed:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchAnalytics()
    return () => {
      cancelled = true
    }
  }, [token])

  const vol = data?.daily_volume ?? []
  const lat = data?.daily_latency ?? []
  const wh = data?.daily_webhooks ?? []
  const rej = data?.rejection_breakdown ?? []
  const funnel = data?.funnel ?? []
  const total = sum(vol.map(d => d.total))
  const verified = sum(vol.map(d => d.verified))
  const failed = sum(vol.map(d => d.failed))
  const delivered = sum(wh.map(d => d.delivered))
  const whFailed = sum(wh.map(d => d.failed))
  const p50 = lat.filter(d => d.p50 > 0)
  const avgP50 = p50.length ? Math.round(sum(p50.map(d => d.p50)) / p50.length) : null
  const funnelMax = Math.max(1, ...funnel.map(f => f.count))
  const rejMax = Math.max(1, ...rej.map(r => r.count))
  const quota = data?.quota

  const exportCsv = () => {
    const rows = [['date', 'total', 'verified', 'failed', 'success_rate', 'p50_ms', 'p95_ms', 'webhooks_delivered', 'webhooks_failed']]
    vol.forEach(d => {
      const l = lat.find(x => x.date === d.date), w = wh.find(x => x.date === d.date)
      rows.push([d.date, d.total, d.verified, d.failed, d.success_rate, l?.p50 ?? '', l?.p95 ?? '', w?.delivered ?? '', w?.failed ?? ''].map(String))
    })
    const url = URL.createObjectURL(new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' }))
    const a = document.createElement('a'); a.href = url; a.download = 'saflens-analytics-7d.csv'; a.click(); URL.revokeObjectURL(url)
  }

  return (
    <div className="sf-fade">
      <div className="sf-toolbar">
        <span className="sf-chip dark" style={{ height: 42, padding: '0 16px', fontSize: 13 }}>Last 7 days</span>
        <PillButton icon={<ArrowDownTrayIcon />} onClick={exportCsv} disabled={!vol.length}>Download data</PillButton>
      </div>

      <div className="sf-grid c4">
        <FolderStatCard label="Sessions" tone="accent" loading={loading} value={total.toLocaleString()} caption="Success rate" captionValue={total ? `${Math.round((verified / total) * 1000) / 10}%` : 'No sessions yet'} />
        <FolderStatCard label="Verified" loading={loading} value={verified.toLocaleString()} caption="Failed" captionValue={failed.toLocaleString()} />
        <FolderStatCard label="Response time" loading={loading} value={avgP50 ?? '—'} unit={avgP50 !== null ? 'ms' : undefined} caption="Median (p50)" captionValue={avgP50 !== null ? 'Average across days' : 'No API calls yet'} />
        <FolderStatCard label="Webhooks" loading={loading} value={delivered.toLocaleString()} unit="sent" caption="Failed deliveries" captionValue={whFailed.toLocaleString()} />
      </div>

      <div className="sf-grid main sf-section">
        <Card>
          <CardHeader title="Verification volume" sub="Verified vs failed per day" />
          {loading ? <Skeleton h={250} r={20} /> : total > 0 ? (
            <>
              <LineChart labels={vol.map(d => shortDay(d.date))} series={[
                { key: 'v', label: 'Verified', tone: 'accent', values: vol.map(d => d.verified) },
                { key: 'f', label: 'Failed', tone: 'ink', values: vol.map(d => d.failed) },
              ]} height={250} ariaLabel="Verified and failed verifications per day" />
              <div style={{ marginTop: 10 }}><Legend items={[{ label: 'Verified', tone: 'accent' }, { label: 'Failed', tone: 'ink' }]} /></div>
            </>
          ) : <EmptyState icon={<ChartBarIcon />} title="No verifications in the last 7 days">Volume appears as soon as sessions are created.</EmptyState>}
        </Card>
        <Card>
          <CardHeader title="Rejection reasons" sub="Share of failed checks" />
          {loading ? <Skeleton h={220} r={20} /> : rej.length ? (
            <div className="sf-list">
              {rej.slice(0, 6).map((r, i) => (
                <div className="sf-row" key={r.reason}>
                  <span className={`sf-roundicon ${i === 0 ? 'solid' : 'accent'}`}><XCircleIcon /></span>
                  <div className="sf-row-main">
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span className="sf-row-title">{humanReason(r.reason)}</span>
                      <span className="sf-row-end"><b style={{ color: 'var(--sf-ink)' }}>{r.count}</b> · {Math.round(r.percentage)}%</span>
                    </div>
                    <Progress value={r.count} max={rejMax} dark={i > 0} />
                  </div>
                </div>
              ))}
            </div>
          ) : <EmptyState icon={<XCircleIcon />} title="No rejections">Nothing has been rejected this week.</EmptyState>}
        </Card>
      </div>

      <div className="sf-grid c3 sf-section">
        <Card>
          <CardHeader title="API response time" sub="p50 and p95, milliseconds" />
          {loading ? <Skeleton h={210} r={20} /> : p50.length ? (
            <>
              <LineChart labels={lat.map(d => shortDay(d.date))} series={[
                { key: 'p50', label: 'p50', tone: 'accent', values: lat.map(d => d.p50) },
                { key: 'p95', label: 'p95', tone: 'ink', values: lat.map(d => d.p95) },
              ]} height={210} formatValue={v => `${Math.round(v)}`} ariaLabel="API response time per day" />
              <div style={{ marginTop: 10 }}><Legend items={[{ label: 'p50 ms', tone: 'accent' }, { label: 'p95 ms', tone: 'ink' }]} /></div>
            </>
          ) : <EmptyState icon={<ClockIcon />} title="No API calls yet" />}
        </Card>

        <Card>
          <CardHeader title="Verification funnel" sub="Conversion by step" />
          {loading ? <Skeleton h={210} r={20} /> : funnel.some(f => f.count > 0) ? (
            <div className="sf-list">
              {funnel.map(f => {
                const meta = STEP_META[f.step] ?? { label: f.step.replace(/_/g, ' '), icon: <SparklesIcon /> }
                return (
                  <div className="sf-row" key={f.step}>
                    <span className={`sf-roundicon ${f.step === 'completed' ? 'solid' : 'accent'}`}>{meta.icon}</span>
                    <div className="sf-row-main">
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span className="sf-row-title">{meta.label}</span>
                        <span className="sf-row-end"><b style={{ color: 'var(--sf-ink)' }}>{f.count}</b> · {Math.round(f.percentage)}%</span>
                      </div>
                      <Progress value={f.count} max={funnelMax} dark={f.step === 'completed'} />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : <EmptyState icon={<SparklesIcon />} title="No funnel data yet" />}
        </Card>

        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <CardHeader title="Quota usage" sub={quota?.limit === null ? 'No monthly limit' : 'This month'} />
          <div style={{ flex: 1, display: 'grid', placeItems: 'center' }}>
            {loading ? <Skeleton h={180} w={180} r={999} /> : quota ? (
              <ProgressRing value={quota.limit === null ? 0 : quota.used} max={quota.limit ?? 1} size={184}>
                <b>{quota.limit === null ? '∞' : `${Math.round((quota.used / Math.max(1, quota.limit)) * 100)}%`}</b>
                <span>{quota.limit === null ? `${quota.used.toLocaleString()} used · unlimited` : `${quota.used.toLocaleString()} of ${quota.limit.toLocaleString()}`}</span>
              </ProgressRing>
            ) : <EmptyState title="No quota data" />}
          </div>
        </Card>
      </div>

      <Card className="sf-section">
        <CardHeader title="Webhook deliveries" sub="Delivered vs failed per day" action={<Legend items={[{ label: 'Delivered', tone: 'accent' }, { label: 'Failed', tone: 'ink' }]} />} />
        {loading ? <Skeleton h={220} r={20} /> : delivered + whFailed > 0 ? (
          <StackedBarChart labels={wh.map(d => shortDay(d.date))} series={[
            { key: 'd', label: 'Delivered', tone: 'accent', values: wh.map(d => d.delivered) },
            { key: 'f', label: 'Failed', tone: 'ink', values: wh.map(d => d.failed) },
          ]} height={230} ariaLabel="Webhook deliveries per day" />
        ) : <EmptyState icon={<BoltIcon />} title="No webhook deliveries">Add an endpoint under Webhooks to receive events.</EmptyState>}
      </Card>
    </div>
  )
}
